"use client";

import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const features = [{
  number: "01",
  title: "Importância de um Desempenho de Pulverização Consistente na Produção, Limpeza e Embalagem de Destilaria",
  body: "Fermentadores, cubas de mosto, cozedores, alambiques, tanques, barris e linhas de engarrafamento requerem uma limpeza completa para remover resíduos e preparar o equipamento para o lote seguinte.\nA Spraying Systems Co. ajuda as destilarias a otimizar operações críticas de pulverização em toda a instalação, incluindo limpeza de tanques e recipientes, lavagem de equipamentos, lavagem de pavimentos e áreas de produção, secagem de garrafas e sopro em linhas de embalagem.",
  image: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-17.47.10.png?v=8kv74gfwrea",
  srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-17.47.10.png?v=bhg5cxxsg58-wvc-srcset",
  sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-17.47.10.png?v=568co3hv8ew-wvc-sizes",
  alt: "Sistemas de engenharia industrial de alta precisão em ambiente controlado",
  imageLeft: true,
  href: "/ProductsPage"
}, {
  number: "02",
  title: "Desafios Comuns nas Operações de Destilaria",
  body: "As destilarias devem limpar recipientes, equipamentos e linhas de engarrafamento de forma eficiente, mantendo horários de produção consistentes. Quando os processos de limpeza ou secagem são lentos, inconsistentes ou demasiado manuais, podem originar tempos de rotação mais longos, aumento de mão de obra, maior consumo de água e de produtos químicos, e tempos de paragem desnecessários. Sujidades persistentes como resíduos de mosto, açúcares, óleos e depósitos minerais podem ser difíceis de remover sem o impacto e a cobertura de pulverização adequados.\nOs desafios comuns incluem:\n\nTempo de rotação lento dos recipientes entre lotes.\nResíduos de mosto, açúcares, óleos e depósitos minerais que permanecem após a limpeza.\nProcessos de limpeza manuais que exigem entrada laboriosa em espaços confinados.\nLimpeza inconsistente de fermentadores, alambiques, tanques, barris e zonas de difícil acesso.\nConsumo excessivo de água, produtos químicos ou energia durante a limpeza e lavagem.\nGarrafas ou latas húmidas que interferem com a rotulagem, codificação ou embalagem.\nPreocupações de segurança relacionadas com mangueiras, pavimentos escorregadios e lavagem manual.",
  image: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-17.47.17.png?v=sws3ksp2u5o",
  srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-17.47.17.png?v=p9yix8w69y-wvc-srcset",
  sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-17.47.17.png?v=qes8hn89te7-wvc-sizes",
  alt: "Bicos de pulverização industrial em operação de alta performance",
  imageLeft: false,
  href: "/NozzlesPulverizersPage"
}, {
  number: "03",
  title: "As Nossas Soluções para Destilarias",
  body: "A Spraying Systems Co. oferece uma gama completa de produtos de pulverização e soluções de engenharia para ambientes de produção de bebidas espirituosas. Os nossos produtos são concebidos para ajudar as destilarias a limpar de forma mais eficaz, melhorar a secagem de garrafas e latas, reduzir o consumo de recursos e apoiar práticas de sanitização consistentes.\nCom as nossas soluções orientadas para destilarias, pode:\n\nLimpar de forma mais completa recipientes de fermentação, alambiques, tanques e barris.\nReduzir a limpeza manual e limitar a entrada em espaços confinados.\nMelhorar o impacto de limpeza em resíduos persistentes de mosto, açúcar, óleo e minerais.\nLavar de forma eficiente pavimentos, equipamentos, transportadores, enchedoras e áreas de produção.\nMelhorar a secagem antes da rotulagem, codificação e embalagem secundária.\nReduzir o consumo de ar comprimido, água e produtos químicos.\nMelhorar a repetibilidade nos processos de limpeza e sanitização.",
  image: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-17.47.23.png?v=57ytuu76tiy",
  srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-17.47.23.png?v=yjcxfujxkej-wvc-srcset",
  sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-05-as-17.47.23.png?v=d680wb1e9j-wvc-sizes",
  alt: "Tecnologia industrial focada em sustentabilidade e eficiência energética",
  imageLeft: true,
  href: "/SustainabilityPage"
}];
export default function PremiumContentShowcase() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".vlt-eyebrow", {
        y: 16
      });
      gsap.set(".vlt-heading", {
        y: 24
      });
      gsap.to(".vlt-eyebrow", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".vlt-header",
          start: "top 85%",
          once: true
        }
      });
      gsap.to(".vlt-heading", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: "power3.out",
        delay: 0.08,
        scrollTrigger: {
          trigger: ".vlt-header",
          start: "top 85%",
          once: true
        }
      });
      const rows = gsap.utils.toArray<HTMLElement>(".vlt-row");
      rows.forEach(row => {
        const image = row.querySelector(".vlt-image");
        const textEls = row.querySelectorAll(".vlt-text > *");
        if (image) {
          gsap.set(image, {
            scale: 1.04,
            y: 40
          });
          gsap.to(image, {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 1.1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: row,
              start: "top 80%",
              once: true
            }
          });
        }
        gsap.set(textEls, {
          y: 24
        });
        gsap.to(textEls, {
          opacity: 1,
          y: 0,
          duration: 0.75,
          ease: "power3.out",
          stagger: 0.08,
          scrollTrigger: {
            trigger: row,
            start: "top 78%",
            once: true
          }
        });
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".vlt-eyebrow", ".vlt-heading", ".vlt-image", ".vlt-row .vlt-text > *"], {
        opacity: 1,
        y: 0,
        scale: 1
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="31429" id="premiumcontentshowcase" data-nav="light" className="bg-background text-foreground py-32 md:py-44 relative overflow-hidden">
      <div ref={container} className="mx-auto max-w-[1360px] px-5 md:px-8 lg:px-14">
        {}
        <div className="vlt-header mx-auto max-w-3xl text-center mb-20 md:mb-24">
          <div className="vlt-eyebrow inline-block text-xs font-medium uppercase mb-6" style={{
          opacity: 0,
          letterSpacing: "0.22em",
          color: "var(--muted-foreground)"
        }}>
            Especialidades
          </div>
          <h2 className="vlt-heading font-default font-medium text-[clamp(2.25rem,5.2vw,5rem)] leading-[0.98]" style={{
          opacity: 0,
          letterSpacing: "-0.028em",
          color: "var(--foreground)"
        }}>
            Excelência em Engenharia.
          </h2>
        </div>

        {}
        <div className="flex flex-col gap-24 md:gap-32">
          {features.map((f, i) => <div key={f.number} data-index={i} className="vlt-row grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center">
              {}
              <div className={`${f.imageLeft ? "md:col-span-6 md:order-1" : "md:col-span-6 md:order-2"} order-1`}>
                <div className="vlt-image relative overflow-hidden rounded-3xl" style={{
              opacity: 0,
              height: "clamp(380px, 55vw, 640px)",
              background: "var(--muted)",
              border: "1px solid var(--border)"
            }}>
                  <img src={f.image} data-wvc-srcset={f.srcset} data-wvc-sizes={f.sizes} alt={f.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                  {}
                  <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-40" style={{
                background: "linear-gradient(180deg, transparent 0%, var(--accent) 100%)",
                filter: "blur(24px)"
              }} />
                  <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-px" style={{
                background: "linear-gradient(90deg, transparent 0%, var(--primary) 50%, transparent 100%)"
              }} />
                </div>
              </div>

              {}
              <div className={`${f.imageLeft ? "md:col-span-6 md:order-2 md:pl-8 lg:pl-16" : "md:col-span-6 md:order-1 md:pr-8 lg:pr-16"} order-2 vlt-text flex flex-col`}>
                <div className="text-xs font-medium uppercase mb-5" style={{
              opacity: 0,
              letterSpacing: "0.22em",
              color: "var(--primary)"
            }}>
                  {f.number}
                </div>
                <h3 className="font-default font-medium text-[clamp(1.5rem,2.4vw,2.25rem)] leading-[1.08] mb-5" style={{
              opacity: 0,
              letterSpacing: "-0.02em",
              color: "var(--foreground)"
            }}>
                  {f.title}
                </h3>
                <p className="font-default font-normal text-[clamp(1.0625rem,1.15vw,1.25rem)] leading-[1.55] mb-8 max-w-xl" style={{
              opacity: 0,
              letterSpacing: "-0.005em",
              color: "var(--muted-foreground)"
            }}>
                  {f.body}
                </p>
                <div style={{
              opacity: 0
            }}>
                  <Button asChild variant="ghost" className="group h-auto p-0 hover:bg-transparent">
                    <Link to={f.href} className="inline-flex items-center gap-3 font-default text-base font-normal" style={{
                  color: "var(--foreground)"
                }}>
                      <span>Saber mais</span>
                      <span className="inline-flex items-center justify-center rounded-lg transition-all duration-200 group-hover:translate-x-1" style={{
                    width: "28px",
                    height: "28px",
                    border: "1px solid var(--border)",
                    color: "var(--primary)"
                  }}>
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </span>
                    </Link>
                  </Button>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}